"use client";

import React, { useState, useRef, useCallback } from "react";
import { toast } from "react-hot-toast";
import { ContentKind, EncodeOptions, EncodedQr } from "@/lib/qr/types";
import { toPayload, getRecommendedECL } from "@/lib/qr/builders";
import { trpc } from "@/lib/trpc";
import { QRState, INITIAL_STATE } from "./components/types";
import QRForm from "./components/QRForm";
import QRPreview from "./components/QRPreview";
import DynamicQR from "./components/DynamicQR";

export default function QRPage() {
  const [state, setState] = useState<QRState>(INITIAL_STATE);
  const [qrResult, setQrResult] = useState<EncodedQr | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copiedPayload, setCopiedPayload] = useState(false);
  const [copiedImageUrl, setCopiedImageUrl] = useState(false);
  const [copiedDynamicUrl, setCopiedDynamicUrl] = useState(false);
  const [dynamicQr, setDynamicQr] = useState<any>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  
  const qrContainerRef = useRef<HTMLDivElement | null>(null);

  // tRPC mutations
  const generateQrMutation = trpc.qr.generate.useMutation();
  const createDynamicQrMutation = trpc.qrDynamic.create.useMutation();

  // Update state helper
  const updateState = useCallback((update: Partial<QRState>) => {
    setState(prev => ({ ...prev, ...update }));
  }, []);

  // Get user's current location
  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by this browser');
      return;
    }

    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude.toString();
        const lng = position.coords.longitude.toString();
        updateState({
          content: {
            ...state.content,
            lat,
            lng,
          }
        });
        toast.success('Location obtained successfully!');
        setIsGettingLocation(false);
      },
      (error) => {
        let errorMessage = 'Failed to get location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
        }
        toast.error(errorMessage);
        setIsGettingLocation(false);
      }
    );
  }, [state.content, updateState]);

  // Generate QR Code
  const generateQR = useCallback(async () => {
    // Validation
    if (state.kind === 'wifi' && !state.content.ssid) {
      toast.error('Please enter a WiFi network name (SSID)');
      return;
    }
    if (state.kind === 'url' && !state.content.url) {
      toast.error('Please enter a URL');
      return;
    }
    if (state.kind === 'text' && !state.content.text) {
      toast.error('Please enter text content');
      return;
    }
    if (state.kind === 'mailto' && !state.content.to) {
      toast.error('Please enter an email address');
      return;
    }
    if (state.kind === 'tel' && !state.content.to) {
      toast.error('Please enter a phone number');
      return;
    }
    if (state.kind === 'sms' && !state.content.to) {
      toast.error('Please enter a phone number');
      return;
    }
    if (state.kind === 'vcard' && !state.content.firstName && !state.content.lastName) {
      toast.error('Please enter at least a first name or last name');
      return;
    }
    if (state.kind === 'mms' && !state.content.to) {
      toast.error('Please enter a phone number for MMS');
      return;
    }
    if (state.kind === 'whatsapp' && !state.content.to) {
      toast.error('Please enter a phone number for WhatsApp');
      return;
    }
    if (state.kind === 'telegram' && !state.content.username) {
      toast.error('Please enter a Telegram username');
      return;
    }
    if (state.kind === 'facetime' && !state.content.to) {
      toast.error('Please enter a phone number or email for FaceTime');
      return;
    }
    if (state.kind === 'skype' && !state.content.username) {
      toast.error('Please enter a Skype username');
      return;
    }
    if (state.kind === 'sip' && !state.content.to) {
      toast.error('Please enter a SIP address');
      return;
    }
    if (state.kind === 'ussd' && !state.content.code) {
      toast.error('Please enter a USSD code');
      return;
    }
    if (state.kind === 'epc' && !state.content.iban) {
      toast.error('Please enter an IBAN');
      return;
    }
    if (state.kind === 'gs1-digital-link' && !state.content.uri) {
      toast.error('Please enter a GS1 Digital Link URI');
      return;
    }
    if (state.kind === 'bitcoin' && !state.content.address) {
      toast.error('Please enter a Bitcoin address');
      return;
    }
    if (state.kind === 'calendar' && !state.content.summary) {
      toast.error('Please enter an event summary');
      return;
    }
    if (state.kind === 'calendar' && !state.content.start) {
      toast.error('Please enter a start date/time');
      return;
    }
    if (state.kind === 'calendar' && !state.content.end) {
      toast.error('Please enter an end date/time');
      return;
    }
    if (state.kind === 'geo' && (!state.content.lat || !state.content.lng)) {
      toast.error('Please enter both latitude and longitude');
      return;
    }
    if (state.kind === 'geo' && (isNaN(parseFloat(state.content.lat)) || isNaN(parseFloat(state.content.lng)))) {
      toast.error('Please enter valid numeric coordinates');
      return;
    }

    setIsGenerating(true);
    try {
      const content: ContentKind = { kind: state.kind, ...state.content } as ContentKind;
      const payload = toPayload(content);
      
      // Get recommended ECL if not set (only show recommendation for complex content)
      const recommendedECL = getRecommendedECL(payload);
      if (state.ecl === 'M' && recommendedECL !== 'M' && payload.length > 100) {
        toast.success(`Tip: Consider using error correction level ${recommendedECL} for better reliability`);
      }

      // Structure the request body based on content type
      let requestBody: any = { 
        kind: state.kind,
        ecl: state.ecl,
        width: state.width,
        marginModules: state.marginModules,
        colorDark: state.colorDark,
        colorLight: state.colorLight,
        svg: state.svg,
      };
      
      if (state.kind === 'wifi') {
        requestBody = { 
          ...requestBody, 
          ssid: state.content.ssid, 
          password: state.content.password, 
          hidden: state.content.hidden, 
          auth: state.content.auth 
        };
      } else if (state.kind === 'vcard') {
        requestBody = { 
          ...requestBody, 
          firstName: state.content.firstName, 
          lastName: state.content.lastName, 
          org: state.content.org, 
          title: state.content.title, 
          tel: state.content.tel, 
          email: state.content.email, 
          url: state.content.url 
        };
      } else if (state.kind === 'mailto') {
        requestBody = { 
          ...requestBody, 
          to: state.content.to, 
          subject: state.content.subject, 
          body: state.content.body 
        };
      } else if (state.kind === 'sms') {
        requestBody = { 
          ...requestBody, 
          to: state.content.to, 
          body: state.content.body 
        };
      } else if (state.kind === 'tel') {
        requestBody = { 
          ...requestBody, 
          to: state.content.to 
        };
      } else if (state.kind === 'geo') {
        requestBody = { 
          ...requestBody, 
          lat: parseFloat(state.content.lat), 
          lng: parseFloat(state.content.lng), 
          query: state.content.query 
        };
      } else if (state.kind === 'calendar') {
        requestBody = { 
          ...requestBody, 
          summary: state.content.summary, 
          start: state.content.start, 
          end: state.content.end, 
          location: state.content.location, 
          description: state.content.description 
        };
      } else if (state.kind === 'bitcoin') {
        requestBody = { 
          ...requestBody, 
          address: state.content.address, 
          amount: state.content.amount, 
          message: state.content.message 
        };
      } else if (state.kind === 'epc') {
        requestBody = { 
          ...requestBody, 
          name: state.content.name, 
          iban: state.content.iban, 
          amount: state.content.amount, 
          remittance: state.content.remittance 
        };
      } else if (state.kind === 'gs1-digital-link') {
        requestBody = { 
          ...requestBody, 
          uri: state.content.uri 
        };
      } else if (state.kind === 'mms') {
        requestBody = { 
          ...requestBody, 
          to: state.content.to, 
          subject: state.content.subject, 
          body: state.content.body 
        };
      } else if (state.kind === 'whatsapp') {
        requestBody = { 
          ...requestBody, 
          to: state.content.to, 
          text: state.content.text 
        };
      } else if (state.kind === 'telegram') {
        requestBody = { 
          ...requestBody, 
          username: state.content.username, 
          text: state.content.text 
        };
      } else if (state.kind === 'facetime') {
        requestBody = { 
          ...requestBody, 
          to: state.content.to, 
          audio: state.content.audio 
        };
      } else if (state.kind === 'skype') {
        requestBody = { 
          ...requestBody, 
          username: state.content.username, 
          call: state.content.call 
        };
      } else if (state.kind === 'sip') {
        requestBody = { 
          ...requestBody, 
          to: state.content.to, 
          subject: state.content.subject, 
          body: state.content.body 
        };
      } else if (state.kind === 'ussd') {
        requestBody = { 
          ...requestBody, 
          code: state.content.code 
        };
      } else {
        // For url and text, the content is already flat
        requestBody = { ...requestBody, ...state.content };
      }

      const result = await generateQrMutation.mutateAsync(requestBody);
      setQrResult(result);
      toast.success('QR code generated successfully!');
    } catch (error) {
      console.error('QR generation error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to generate QR code');
    } finally {
      setIsGenerating(false);
    }
  }, [state, generateQrMutation]);

  // Create Dynamic QR Code
  const createDynamicQR = useCallback(async () => {
    if (!state.dynamicUrl) {
      toast.error('Please enter target URL for dynamic QR');
      return;
    }

    setIsGenerating(true);
    try {
      const result = await createDynamicQrMutation.mutateAsync({
        targetUrl: state.dynamicUrl,
        customSlug: state.dynamicSlug || undefined,
        expiresAt: state.dynamicExpiresAt || undefined,
        maxScans: state.dynamicMaxScans || undefined,
        password: state.dynamicPassword || undefined,
      });
      
      setDynamicQr(result);
      toast.success('Dynamic QR code created successfully!');
    } catch (error) {
      console.error('Dynamic QR creation error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create dynamic QR');
    } finally {
      setIsGenerating(false);
    }
  }, [state.dynamicUrl, state.dynamicSlug, state.dynamicExpiresAt, state.dynamicMaxScans, state.dynamicPassword, createDynamicQrMutation]);

  // Download functions
  const downloadQR = useCallback(async (format: 'svg' | 'png') => {
    if (!qrResult) return;

    try {
      // Generate new QR code with the requested format
      const content: ContentKind = { kind: state.kind, ...state.content } as ContentKind;
      const options: EncodeOptions = {
        ecl: state.ecl,
        width: state.width,
        marginModules: state.marginModules,
        colorDark: state.colorDark,
        colorLight: state.colorLight,
        svg: format === 'svg',
      };

      // Structure the request body based on content type
      let requestBody: any = { kind: state.kind, ...options };
      
      if (state.kind === 'wifi') {
        requestBody = { 
          ...requestBody, 
          ssid: state.content.ssid, 
          password: state.content.password, 
          hidden: state.content.hidden, 
          auth: state.content.auth 
        };
      } else if (state.kind === 'vcard') {
        requestBody = { 
          ...requestBody, 
          firstName: state.content.firstName, 
          lastName: state.content.lastName, 
          org: state.content.org, 
          title: state.content.title, 
          tel: state.content.tel, 
          email: state.content.email, 
          url: state.content.url 
        };
      } else if (state.kind === 'mailto') {
        requestBody = { 
          ...requestBody, 
          to: state.content.to, 
          subject: state.content.subject, 
          body: state.content.body 
        };
      } else if (state.kind === 'sms') {
        requestBody = { 
          ...requestBody, 
          to: state.content.to, 
          body: state.content.body 
        };
      } else if (state.kind === 'tel') {
        requestBody = { 
          ...requestBody, 
          to: state.content.to 
        };
      } else if (state.kind === 'geo') {
        requestBody = { 
          ...requestBody, 
          lat: parseFloat(state.content.lat), 
          lng: parseFloat(state.content.lng), 
          query: state.content.query 
        };
      } else if (state.kind === 'calendar') {
        requestBody = { 
          ...requestBody, 
          summary: state.content.summary, 
          start: state.content.start, 
          end: state.content.end, 
          location: state.content.location, 
          description: state.content.description 
        };
      } else if (state.kind === 'bitcoin') {
        requestBody = { 
          ...requestBody, 
          address: state.content.address, 
          amount: state.content.amount, 
          message: state.content.message 
        };
      } else if (state.kind === 'epc') {
        requestBody = { 
          ...requestBody, 
          name: state.content.name, 
          iban: state.content.iban, 
          amount: state.content.amount, 
          remittance: state.content.remittance 
        };
      } else if (state.kind === 'gs1-digital-link') {
        requestBody = { 
          ...requestBody, 
          uri: state.content.uri 
        };
      } else if (state.kind === 'mms') {
        requestBody = { 
          ...requestBody, 
          to: state.content.to, 
          subject: state.content.subject, 
          body: state.content.body 
        };
      } else if (state.kind === 'whatsapp') {
        requestBody = { 
          ...requestBody, 
          to: state.content.to, 
          text: state.content.text 
        };
      } else if (state.kind === 'telegram') {
        requestBody = { 
          ...requestBody, 
          username: state.content.username, 
          text: state.content.text 
        };
      } else if (state.kind === 'facetime') {
        requestBody = { 
          ...requestBody, 
          to: state.content.to, 
          audio: state.content.audio 
        };
      } else if (state.kind === 'skype') {
        requestBody = { 
          ...requestBody, 
          username: state.content.username, 
          call: state.content.call 
        };
      } else if (state.kind === 'sip') {
        requestBody = { 
          ...requestBody, 
          to: state.content.to, 
          subject: state.content.subject, 
          body: state.content.body 
        };
      } else if (state.kind === 'ussd') {
        requestBody = { 
          ...requestBody, 
          code: state.content.code 
        };
      } else {
        // For url and text, the content is already flat
        requestBody = { ...requestBody, ...state.content };
      }

      const result = await generateQrMutation.mutateAsync(requestBody);
      
      // Create blob from the result
      const blob = new Blob([Buffer.from(result.dataURL.split(',')[1], 'base64')], { 
        type: result.mime 
      });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `qrcode.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`QR code downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download QR code');
    }
  }, [qrResult, state]);

  // Custom download handler for DownloadComponent
  const handleQRDownload = useCallback(async (format: string, filename: string) => {
    if (!qrResult) return;

    try {
      let blob: Blob;
      let mimeType: string;

      // Handle different formats
      if (format === 'svg') {
        // Generate SVG format
        const content: ContentKind = { kind: state.kind, ...state.content } as ContentKind;
        const options: EncodeOptions = {
          ecl: state.ecl,
          width: state.width,
          marginModules: state.marginModules,
          colorDark: state.colorDark,
          colorLight: state.colorLight,
          svg: true,
        };

        // Structure the request body based on content type
        let requestBody: any = { kind: state.kind, ...options };
        
        if (state.kind === 'wifi') {
          requestBody = { 
            ...requestBody, 
            ssid: state.content.ssid, 
            password: state.content.password, 
            hidden: state.content.hidden, 
            auth: state.content.auth 
          };
        } else if (state.kind === 'vcard') {
          requestBody = { 
            ...requestBody, 
            firstName: state.content.firstName, 
            lastName: state.content.lastName, 
            org: state.content.org, 
            title: state.content.title, 
            tel: state.content.tel, 
            email: state.content.email, 
            url: state.content.url 
          };
        } else if (state.kind === 'mailto') {
          requestBody = { 
            ...requestBody, 
            to: state.content.to, 
            subject: state.content.subject, 
            body: state.content.body 
          };
        } else if (state.kind === 'sms') {
          requestBody = { 
            ...requestBody, 
            to: state.content.to, 
            body: state.content.body 
          };
        } else if (state.kind === 'tel') {
          requestBody = { 
            ...requestBody, 
            to: state.content.to 
          };
        } else if (state.kind === 'geo') {
          requestBody = { 
            ...requestBody, 
            lat: parseFloat(state.content.lat), 
            lng: parseFloat(state.content.lng), 
            query: state.content.query 
          };
        } else if (state.kind === 'calendar') {
          requestBody = { 
            ...requestBody, 
            summary: state.content.summary, 
            start: state.content.start, 
            end: state.content.end, 
            location: state.content.location, 
            description: state.content.description 
          };
        } else if (state.kind === 'bitcoin') {
          requestBody = { 
            ...requestBody, 
            address: state.content.address, 
            amount: state.content.amount, 
            message: state.content.message 
          };
        } else if (state.kind === 'epc') {
          requestBody = { 
            ...requestBody, 
            name: state.content.name, 
            iban: state.content.iban, 
            amount: state.content.amount, 
            remittance: state.content.remittance 
          };
        } else if (state.kind === 'gs1-digital-link') {
          requestBody = { 
            ...requestBody, 
            uri: state.content.uri 
          };
        } else if (state.kind === 'mms') {
          requestBody = { 
            ...requestBody, 
            to: state.content.to, 
            subject: state.content.subject, 
            body: state.content.body 
          };
        } else if (state.kind === 'whatsapp') {
          requestBody = { 
            ...requestBody, 
            to: state.content.to, 
            text: state.content.text 
          };
        } else if (state.kind === 'telegram') {
          requestBody = { 
            ...requestBody, 
            username: state.content.username, 
            text: state.content.text 
          };
        } else if (state.kind === 'facetime') {
          requestBody = { 
            ...requestBody, 
            to: state.content.to, 
            audio: state.content.audio 
          };
        } else if (state.kind === 'skype') {
          requestBody = { 
            ...requestBody, 
            username: state.content.username, 
            call: state.content.call 
          };
        } else if (state.kind === 'sip') {
          requestBody = { 
            ...requestBody, 
            to: state.content.to, 
            subject: state.content.subject, 
            body: state.content.body 
          };
        } else if (state.kind === 'ussd') {
          requestBody = { 
            ...requestBody, 
            code: state.content.code 
          };
        } else {
          // For url and text, the content is already flat
          requestBody = { ...requestBody, ...state.content };
        }

        const result = await generateQrMutation.mutateAsync(requestBody);
        blob = new Blob([Buffer.from(result.dataURL.split(',')[1], 'base64')], { 
          type: result.mime 
        });
        mimeType = result.mime;
      } else {
        // For other formats, convert the existing QR code
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        return new Promise<void>((resolve, reject) => {
          img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx?.drawImage(img, 0, 0);
            
            // Convert to the requested format
            canvas.toBlob((convertedBlob) => {
              if (!convertedBlob) {
                reject(new Error('Failed to convert image'));
                return;
              }
              
              // Set appropriate MIME type
              switch (format) {
                case 'jpg':
                case 'jpeg':
                  mimeType = 'image/jpeg';
                  break;
                case 'png':
                  mimeType = 'image/png';
                  break;
                case 'webp':
                  mimeType = 'image/webp';
                  break;
                case 'bmp':
                  mimeType = 'image/bmp';
                  break;
                case 'gif':
                  mimeType = 'image/gif';
                  break;
                default:
                  mimeType = 'image/png';
              }
              
              // Create new blob with correct MIME type
              blob = new Blob([convertedBlob], { type: mimeType });
              
              // Download the file
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
              
              toast.success(`QR code downloaded as ${format.toUpperCase()}`);
              resolve();
            }, mimeType, 0.9);
          };
          
          img.onerror = () => reject(new Error('Failed to load image'));
          img.src = qrResult.dataURL;
        });
      }

      // For SVG format, download directly
      if (format === 'svg') {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast.success(`QR code downloaded as ${format.toUpperCase()}`);
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download QR code');
    }
  }, [qrResult, state, generateQrMutation]);

  // Copy to clipboard functions
  const copyPayload = useCallback(async () => {
    if (!qrResult?.payload) return;
    try {
      await navigator.clipboard.writeText(qrResult.payload);
      setCopiedPayload(true);
      toast.success('Payload copied to clipboard!');
      setTimeout(() => setCopiedPayload(false), 2000);
    } catch (error) {
      toast.error('Failed to copy payload');
    }
  }, [qrResult?.payload]);

  const copyImageUrl = useCallback(async () => {
    if (!qrResult?.dataURL) return;
    try {
      await navigator.clipboard.writeText(qrResult.dataURL);
      setCopiedImageUrl(true);
      toast.success('Image URL copied to clipboard!');
      setTimeout(() => setCopiedImageUrl(false), 2000);
    } catch (error) {
      toast.error('Failed to copy image URL');
    }
  }, [qrResult?.dataURL]);

  const copyDynamicUrl = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedDynamicUrl(true);
      toast.success('Dynamic URL copied to clipboard!');
      setTimeout(() => setCopiedDynamicUrl(false), 2000);
    } catch (error) {
      toast.error('Failed to copy dynamic URL');
    }
  }, []);

  // Reset state
  const resetState = useCallback(() => {
    setState(INITIAL_STATE);
    setQrResult(null);
    setDynamicQr(null);
    toast.success('Form reset');
  }, []);

  return (
    <div className="min-h-screen bg-linear-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold bg-linear-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
              QR Code Generator
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Create professional QR codes for URLs, WiFi networks, contacts, and more. 
              Generate static or dynamic QR codes with advanced customization options.
            </p>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Form */}
            <div className="space-y-6">
              <QRForm
                state={state}
                updateState={updateState}
                onGenerate={generateQR}
                onReset={resetState}
                isGenerating={isGenerating}
                isGettingLocation={isGettingLocation}
                onGetLocation={getCurrentLocation}
              />
              
              <DynamicQR
                state={state}
                updateState={updateState}
                onCreateDynamic={createDynamicQR}
                isGenerating={isGenerating}
              />
            </div>

            {/* Right Column - Preview */}
            <QRPreview
              qrResult={qrResult}
              state={state}
              dynamicQr={dynamicQr}
              copiedPayload={copiedPayload}
              copiedImageUrl={copiedImageUrl}
              copiedDynamicUrl={copiedDynamicUrl}
              onCopyPayload={copyPayload}
              onCopyImageUrl={copyImageUrl}
              onCopyDynamicUrl={copyDynamicUrl}
              onDownload={handleQRDownload}
            />
          </div>
        </div>
      </div>
    </div>
  );
}